import { useCallback } from 'react'

import { create } from '@bufbuild/protobuf'
import { createQueryOptions, useTransport } from '@connectrpc/connect-query'
import type { ApprovalTask } from '@ebalance/proto/bffuser/v1/approval_task_model_pb'
import { listBiddingIntradayOrders } from '@ebalance/proto/bffuser/v1/bidding_intraday_service-BiddingIntradayService_connectquery'
import { ListBiddingIntradayOrdersRequestSchema } from '@ebalance/proto/bffuser/v1/bidding_intraday_service_pb'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import type { ViolationSection } from '~/components/bidding/common/SubmitConfirmModal'
import { convertListIntradayResponse } from '~/hooks/bidding/useListBiddingIntradayOrders'
import {
  buildIntradayBidLimitViolationSection,
  extractIntradayBidLimitViolationDetails,
  intradayViolationSourceBidOf,
} from '~/pages/IntradayBidding/models/intradaySubmitViolation'

/**
 * 時間前入札の承認が入札制限違反で断られた時に、 時間前入札画面の承認確認モーダルと同じ違反サマリ
 * (違反件数・違反行の詳細: 対象コマ・エリア、 設定上限/下限、 実際の入札値。 AC-BIN-097) を組む。
 *
 * 断った理由 (ErrorInfo の metadata) には設定側の値だけが載るので、 実際の入札値は同じ受渡日の
 * 注文の一覧から対象の札の行を引いて補う。 一覧を読めない・対象の行が無い・価格か数量が取れない時は
 * undefined を返し、 呼び出し元が理由だけを伝える文に落とす。
 */
export function useIntradayViolationSection(): (
  task: ApprovalTask,
  err: unknown,
) => Promise<ViolationSection | undefined> {
  const { t } = useTranslation()
  const transport = useTransport()
  const queryClient = useQueryClient()

  return useCallback(
    async (task, err) => {
      try {
        const response = await queryClient.fetchQuery({
          ...createQueryOptions(
            listBiddingIntradayOrders,
            create(ListBiddingIntradayOrdersRequestSchema, {
              targetDateFilter: task.targetDate,
            }),
            { transport },
          ),
          // 承認を断られた直後の判断材料なので、 古い一覧は使わない。
          staleTime: 0,
        })
        const order = convertListIntradayResponse(response).find(
          (row) => row.id === task.id,
        )
        const bid =
          order === undefined ? undefined : intradayViolationSourceBidOf(order)
        if (bid === undefined) return undefined
        const details = extractIntradayBidLimitViolationDetails(err, bid)
        return details.length === 0
          ? undefined
          : buildIntradayBidLimitViolationSection(details, 0, t, 'approve')
      } catch {
        return undefined
      }
    },
    [queryClient, t, transport],
  )
}
